import { set, lensIndex } from 'ramda';
import { ClientFlowAction } from '~/graphql/types.client';
import { reportAndThrow } from '~/util/reportAndThrow';

type Opts = {
  /**
   * Where did the edge start
   */
  source: string;

  /**
   * Where did the edge end
   */
  target: string;

  /**
   * The action to insert between source and target
   */
  subject: ClientFlowAction;

  /**
   * Is paste target the true path. If it is not passed, the action is connected to IfElse's true path
   */
  isPasteTruePath?: boolean;
};
const onUpdateActions = (
  actions: Array<ClientFlowAction>,
  { source, target, subject: _subject, isPasteTruePath }: Opts,
): Array<ClientFlowAction> => {
  const subject = { ..._subject };

  if (subject.__typename === 'FlowV2_Action_Start') {
    return reportAndThrow('Inserting a start action is not supported');
  }

  const newParentActionIndex = actions.findIndex(({ id }) => id === source);
  let newParentAction = actions[newParentActionIndex];

  const newChildActionIndex = actions.findIndex(({ id }) => id === target);
  const newChildAction = actions[newChildActionIndex];

  subject.parentIds = [newParentAction.id];

  if (newParentAction.__typename === 'FlowV2_Action_IfElse') {
    const ifElseKey =
      newParentAction.trueChildId === newChildAction.id
        ? 'trueChildId'
        : 'falseChildId';
    newParentAction = {
      ...newParentAction,
      [ifElseKey]: subject.id,
    };
  }

  const conditionalProps =
    newChildAction.__typename === 'FlowV2_Action_IfElse'
      ? {
          trueChildId: newChildAction.trueChildId,
          falseChildId: newChildAction.falseChildId,
        }
      : {};

  const updatedParentIds =
    'parentIds' in newChildAction ? [...newChildAction.parentIds] : [];
  const indexOfSubject = updatedParentIds.findIndex(id => id === source);

  // It's important to keep the same order of parentIds
  updatedParentIds.splice(indexOfSubject, 1, subject.id);

  const updatedNewChildAction = {
    ...newChildAction,
    ...conditionalProps,
    parentIds: updatedParentIds,
  };

  // Update the actions array
  const updatedActions = set(
    lensIndex(newChildActionIndex),
    updatedNewChildAction,
    // Update parent
    set(lensIndex(newParentActionIndex), newParentAction, actions),
  );

  if (subject.__typename === 'FlowV2_Action_IfElse') {
    const pasteTruePath = isPasteTruePath ?? true;

    const subjectAction = {
      ...subject,
      ...(pasteTruePath
        ? { trueChildId: updatedNewChildAction.id, falseChildId: null }
        : { trueChildId: null, falseChildId: updatedNewChildAction.id }),
    };

    return [
      ...updatedActions,
      {
        ...subjectAction,
        parentIds: [source],
      },
    ];
  }

  return [
    ...updatedActions,
    {
      ...subject,
      parentIds: [source],
    },
  ];
};

export default onUpdateActions;
